import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  LogOut,
  Loader2,
  Lock,
  Check,
  Trash2,
  Phone,
  Mail,
  Inbox,
  CheckCircle2,
  Clock,
  RefreshCw,
  ImageIcon,
  Newspaper,
} from "lucide-react";
import { toast } from "sonner";
import BlogPanel from "./BlogPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "softpro_admin_token";

const authHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` },
});

const PhotoThumb = ({ photo }) => {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let active = true;
    let objectUrl;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/files`, {
          ...authHeader(),
          params: { path: photo.path },
          responseType: "blob",
        });
        if (!active) return;
        objectUrl = URL.createObjectURL(data);
        setUrl(objectUrl);
      } catch {
        if (active) setErr(true);
      }
    })();
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [photo.path]);

  if (err) {
    return (
      <div className="grid h-24 w-24 place-items-center rounded-xl border border-border bg-secondary text-slate-400">
        <ImageIcon className="h-6 w-6" />
      </div>
    );
  }
  if (!url) {
    return (
      <div className="grid h-24 w-24 place-items-center rounded-xl border border-border bg-secondary">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </div>
    );
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" data-testid="admin-photo">
      <img
        src={url}
        alt={photo.filename || "Klantfoto"}
        className="h-24 w-24 rounded-xl border border-border object-cover transition-transform hover:scale-105"
      />
    </a>
  );
};

const LoginScreen = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.user);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Inloggen mislukt");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-2xl border-2 border-slate-200 bg-white px-4 py-3.5 text-ink placeholder:text-slate-400 outline-none transition-colors focus:border-navy";

  return (
    <div className="grid min-h-screen place-items-center bg-secondary px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md rounded-3xl border border-border bg-white p-8 shadow-xl"
        data-testid="admin-login"
      >
        <div className="flex flex-col items-center text-center">
          <img src="/logo.png" alt="SoftPro" className="h-10 w-auto" />
          <div className="mt-6 grid h-12 w-12 place-items-center rounded-2xl bg-navy/10 text-navy">
            <Lock className="h-6 w-6" />
          </div>
          <h1 className="mt-4 font-display text-2xl font-bold text-ink">
            Admin dashboard
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Log in om alle aanvragen te bekijken.
          </p>
        </div>
        <form onSubmit={submit} className="mt-8 space-y-4">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="E-mailadres"
            data-testid="admin-email"
            className={inputCls}
          />
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Wachtwoord"
            data-testid="admin-password"
            className={inputCls}
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-navy px-6 py-4 font-semibold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Inloggen"}
          </button>
        </form>
      </motion.div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, tone }) => (
  <div className="rounded-3xl border border-border bg-white p-5">
    <div className="flex items-center gap-3">
      <span className={`grid h-11 w-11 place-items-center rounded-2xl ${tone}`}>
        <Icon className="h-5 w-5" />
      </span>
      <div>
        <p className="text-2xl font-bold text-ink font-display">{value}</p>
        <p className="text-xs text-muted-foreground">{label}</p>
      </div>
    </div>
  </div>
);

const Dashboard = ({ user, onLogout }) => {
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState({ total: 0, open: 0, handled: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [view, setView] = useState("leads");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        axios.get(`${API}/admin/contacts`, authHeader()),
        axios.get(`${API}/admin/stats`, authHeader()),
      ]);
      setContacts(c.data);
      setStats(s.data);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else toast.error("Laden mislukt");
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleHandled = async (c) => {
    try {
      await axios.patch(`${API}/admin/contacts/${c.id}`, { handled: !c.handled }, authHeader());
      load();
    } catch {
      toast.error("Bijwerken mislukt");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Deze aanvraag verwijderen?")) return;
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, authHeader());
      toast.success("Aanvraag verwijderd");
      load();
    } catch {
      toast.error("Verwijderen mislukt");
    }
  };

  const filtered = contacts.filter((c) =>
    filter === "all" ? true : filter === "open" ? !c.handled : c.handled
  );

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString("nl-NL", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="min-h-screen bg-secondary">
      <header className="sticky top-0 z-10 border-b border-border bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="SoftPro" className="h-8 w-auto" />
            <span className="hidden text-sm text-muted-foreground sm:inline">
              Admin · {user.email}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              data-testid="admin-refresh"
              className="grid h-10 w-10 place-items-center rounded-full border border-border text-ink transition-colors hover:bg-secondary"
              aria-label="Vernieuwen"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
            <button
              onClick={onLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 rounded-full bg-ink px-4 py-2.5 text-sm font-semibold text-white"
            >
              <LogOut className="h-4 w-4" /> Uitloggen
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <div className="mb-8 flex gap-2">
          <button
            onClick={() => setView("leads")}
            data-testid="tab-leads"
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
              view === "leads" ? "bg-navy text-white" : "bg-white text-ink border border-border hover:bg-secondary"
            }`}
          >
            <Inbox className="h-4 w-4" /> Aanvragen
          </button>
          <button
            onClick={() => setView("blog")}
            data-testid="tab-blog"
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
              view === "blog" ? "bg-navy text-white" : "bg-white text-ink border border-border hover:bg-secondary"
            }`}
          >
            <Newspaper className="h-4 w-4" /> Blog
          </button>
        </div>

        {view === "blog" ? (
          <BlogPanel />
        ) : (
        <>
        <h1 className="font-display text-3xl font-bold text-ink">Aanvragen</h1>
        <p className="mt-1 text-muted-foreground">
          Alle offerte-aanvragen en berichten, nieuwste bovenaan.
        </p>

        <div className="mt-6 grid grid-cols-3 gap-4">
          <StatCard icon={Inbox} label="Totaal" value={stats.total} tone="bg-navy/10 text-navy" />
          <StatCard icon={Clock} label="Open" value={stats.open} tone="bg-teal/10 text-teal" />
          <StatCard icon={CheckCircle2} label="Afgehandeld" value={stats.handled} tone="bg-emerald-100 text-emerald-600" />
        </div>

        <div className="mt-8 flex gap-2">
          {[
            { k: "all", label: "Alle" },
            { k: "open", label: "Open" },
            { k: "handled", label: "Afgehandeld" },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setFilter(t.k)}
              data-testid={`admin-filter-${t.k}`}
              className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${
                filter === t.k
                  ? "bg-navy text-white"
                  : "bg-white text-ink border border-border hover:bg-secondary"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-6 space-y-4" data-testid="admin-contacts">
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-navy" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-border bg-white py-20 text-center text-muted-foreground">
              Geen aanvragen in deze categorie.
            </div>
          ) : (
            filtered.map((c) => (
              <div
                key={c.id}
                data-testid={`admin-contact-${c.id}`}
                className={`rounded-3xl border bg-white p-5 sm:p-6 transition-colors ${
                  c.handled ? "border-border opacity-70" : "border-navy/20"
                }`}
              >
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-display text-lg font-bold text-ink">{c.name}</h3>
                      {c.service && (
                        <span className="rounded-full bg-teal/10 px-3 py-1 text-xs font-semibold text-teal">
                          {c.service}
                        </span>
                      )}
                      {c.handled && (
                        <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-600">
                          Afgehandeld
                        </span>
                      )}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-muted-foreground">
                      <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1.5 hover:text-navy">
                        <Phone className="h-4 w-4" /> {c.phone}
                      </a>
                      <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1.5 hover:text-navy">
                        <Mail className="h-4 w-4" /> {c.email}
                      </a>
                      <span className="inline-flex items-center gap-1.5">
                        <Clock className="h-4 w-4" /> {fmtDate(c.created_at)}
                      </span>
                    </div>
                    <p className="mt-3 whitespace-pre-line rounded-2xl bg-secondary p-4 text-sm text-ink/80">
                      {c.message}
                    </p>
                    {c.photos && c.photos.length > 0 && (
                      <div className="mt-3" data-testid={`admin-photos-${c.id}`}>
                        <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          <ImageIcon className="h-4 w-4" /> {c.photos.length} foto('s)
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {c.photos.map((p) => (
                            <PhotoThumb key={p.path} photo={p} />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex shrink-0 gap-2 sm:flex-col">
                    <button
                      onClick={() => toggleHandled(c)}
                      data-testid={`admin-toggle-${c.id}`}
                      className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition-colors ${
                        c.handled
                          ? "bg-secondary text-ink hover:bg-slate-200"
                          : "bg-teal text-white hover:-translate-y-0.5"
                      }`}
                    >
                      <Check className="h-4 w-4" />
                      {c.handled ? "Heropenen" : "Afgehandeld"}
                    </button>
                    <button
                      onClick={() => remove(c.id)}
                      data-testid={`admin-delete-${c.id}`}
                      className="inline-flex items-center justify-center gap-2 rounded-full border border-border px-4 py-2.5 text-sm font-semibold text-red-500 transition-colors hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        </>
        )}
      </main>
    </div>
  );
};

export const AdminDashboard = () => {
  const [authed, setAuthed] = useState(null); // null=checking
  const [user, setUser] = useState(null);

  useEffect(() => {
    document.title = "Admin — SoftPro";
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setAuthed(false);
      return;
    }
    axios
      .get(`${API}/auth/me`, authHeader())
      .then(({ data }) => {
        setUser(data);
        setAuthed(true);
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setAuthed(false);
      });
  }, []);

  const onLogin = (u) => {
    setUser(u);
    setAuthed(true);
  };
  const onLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    setAuthed(false);
  };

  if (authed === null) {
    return (
      <div className="grid min-h-screen place-items-center bg-secondary">
        <Loader2 className="h-8 w-8 animate-spin text-navy" />
      </div>
    );
  }
  return authed ? (
    <Dashboard user={user} onLogout={onLogout} />
  ) : (
    <LoginScreen onLogin={onLogin} />
  );
};

export default AdminDashboard;
